import { useQuery } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';

export interface Context {
  calculations: {
    metric: string;
    fetus: number;
    value: number | null;
    unit: string;
    formula: string;
    status: string;
  }[];
  dating?: { edd: string | null; method: string; revision: string; fetus_count: number };
  dating_warning?: string | null;
  profile?: { revision: number; enabled_formulas: string[] };
  series: Series[];
}
export interface Detail extends Study {
  context: Context | null;
  draft: {
    report_payload?: import('./EditableUltrasoundReport').ReportPayload | null;
    impression?: string;
    recommendations?: string;
    template_id?: string;
    figure_keys?: string[];
    figures?: Series[];
  };
  evidence: { acquired_date?: string; comments?: string; observations?: Observation[]; reviewed?: boolean; extraction_job_id?: string | null; reviewed_extraction?: { job_id: string } | null };
  extraction?: {
    job_id?: string;
    status?: 'queued' | 'downloading' | 'processing' | 'complete' | 'partial' | 'failed';
    total?: number;
    completed?: number;
    candidate_count?: number;
    error_count?: number;
    error?: string;
    flags?: string[];
    table?: { fetus?: string | null; structure?: string; parameter: string; measurements?: number[]; average?: number | null; unit?: string; percentile?: number | null; machine_percentile?: number | null; reference?: string | null; needs_review?: boolean; notes?: string[] }[];
    report_pages?: { sop_instance_uid: string; frame_index?: number | null; fields: { label: string; value: string; confidence: number }[]; text: string }[];
    source_context?: { percentile_ga_days?: number | null; percentile_ga_basis?: string | null };
    pipeline_version?: string;
  };
  attachments?: { id: string; filename: string; content_type: string }[];
  objects: {
    id: string;
    sop_uid: string;
    image_info?: { frames: number; rows: number; source_filename?: string; source_page?: number };
    candidates: { path: string; name: string; value: string; unit: string; method?: string; source?: string; frame?: number | null; fetus_label?: string | null; confidence?: number | null; flags?: string[]; raw_value?: string | null; raw_unit?: string | null; percentile?: number | null }[];
  }[];
}
export interface Observation {
  fetus: number | null;
  metric: string;
  source_note: string;
  source_object_id?: string | null;
  source_path?: string | null;
  unit: string;
  value: number;
}
export interface Series {
  fetus: number | null;
  key: string;
  metric: string;
  points: { study_id: string; date: string; ga_days: number | null; value: number; formula?: string }[];
  reference?: string;
  unit: string;
}
export interface Study {
  description?: string | null;
  draft_revision: number;
  id: string;
  metadata: Record<string, string>;
  patient_id: string | null;
  pregnancy_id: string | null;
  revision: number;
  scheduled_at?: string | null;
  scheduled_timezone?: string | null;
  signed_document_id: string | null;
  status: string;
  study_uid: string | null;
}
export function apiError(error: unknown) {
  const e = error as { response?: { data?: { detail?: unknown; message?: string } } };
  const detail = e.response?.data?.detail;
  if (Array.isArray(detail)) {
    const item = detail.find((entry) => typeof entry?.msg === 'string');
    if (item) return `Check the report fields: ${item.msg}`;
  }
  return typeof detail === 'string'
    ? detail
    : e.response?.data?.message ||
        'Request failed. Your unsaved work is retained; retry or reload to resolve a conflict.';
}
export function useImagingApi() {
  const { user, organization } = useAuth();
  const org = organization?.organization_id;
  const base = `/api/v1/organizations/${org}/imaging`;
  return { org, base, userId: user?.id };
}
export function useImagingCapabilities() {
  const { org, base, userId } = useImagingApi();
  return useQuery({
    enabled: !!org,
    queryKey: ['imaging-capabilities', org, userId],
    queryFn: async () =>
      (await clientApi.get<{ permissions: string[]; transcription_enabled: boolean }>(`${base}/capabilities`)).data,
  });
}
export function useImagingQuery<T>(path: string, enabled = true) {
  const { org, base, userId } = useImagingApi();
  return useQuery({
    enabled: !!org && enabled,
    queryKey: ['imaging', org, userId, path],
    queryFn: async () => (await clientApi.get<T>(base + path)).data,
  });
}
